import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import { Fragment, useEffect, useState } from 'react';
import { RefreshRounded } from '@mui/icons-material';
import { DateTime } from 'luxon';
import LoadingWrapper from '../../components/LoadingWrapper';
import { useCompany, useReceipt, useAuth } from '../../stores';
import { Money } from '../../utils/currency';
import Receipt from '../../model/Receipt';
import { DefaultNamesForPaymentMethodConfigTypes } from '../../model/PaymentMethodConfig';
import { useRegisterZHistory } from '../../stores/FetcherStores/ZHistoryFetcher';
import { groupReceiptsByRegister, ReceiptRegisterSection } from '../../utils/receiptHistory';
import { timestampToDate } from '../../utils/date';
import { getUnitType } from '../../utils/product';

const ROWS_PER_PAGE = 15;

const ReceiptSection = observer(
  ({
    section,
    showRegisterInfo,
  }: {
    section: ReceiptRegisterSection;
    showRegisterInfo: boolean;
  }) => {
    const receiptStore = useReceipt();
    const companyStore = useCompany();
    const auth = useAuth();
    const [page, setPage] = useState(0);
    const [openKey, setOpenKey] = useState<string | null>(null);
    const activatedAt = timestampToDate(section.register?.activatedAt);

    const getReceiptPaymentMethod = (receipt: Receipt) => {
      if (receipt.paymentMethods) {
        return receipt.paymentMethods
          .map((paymentMethod) => {
            return (
              DefaultNamesForPaymentMethodConfigTypes[paymentMethod.type] || paymentMethod.type
            );
          })
          .join(', ');
      }
      return '-';
    };

    const content = (
      <Box>
        {section.receipts.length === 0 ? (
          <Typography>Det finns inga inlästa kvitton för detta kassaregister.</Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label={`Kvitton ${section.label}`}>
              <TableHead>
                <TableRow>
                  <TableCell>Id</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell align="right">Summa</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {section.receipts
                  .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
                  .map((receipt: Receipt) => (
                    <Fragment key={receipt.id}>
                      <TableRow sx={{ '& > td': { borderBottom: 'none' }, p: 0 }}>
                        <TableCell>
                          {receipt.receiptId ? receipt.receiptId : receipt.webshopOrderId}
                        </TableCell>
                        <TableCell>
                          {receipt.dateCreated
                            ? DateTime.fromISO(receipt.dateCreated).toFormat('yyyy-MM-dd HH:mm')
                            : DateTime.fromISO(receipt.datePaid).toFormat('yyyy-MM-dd HH:mm')}
                        </TableCell>
                        <TableCell align="right">{new Money(receipt.total).format()}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            aria-label={`Visa kvitto ${
                              receipt.receiptId ?? receipt.webshopOrderId ?? receipt.id
                            }`}
                            aria-expanded={receipt.id === openKey}
                            size="small"
                            onClick={() => {
                              if (openKey === receipt.id) {
                                setOpenKey(null);
                              } else {
                                setOpenKey(receipt.id);
                              }
                            }}
                          >
                            {receipt.id === openKey ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={receipt.id === openKey} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                              <Box
                                sx={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                  display: 'flex',
                                }}
                              >
                                <Typography variant="h5">Kvitto #{receipt.receiptId}</Typography>
                                {auth.isAdmin && (
                                  <Button
                                    variant="contained"
                                    target="_blank"
                                    href={receiptStore.getFirebaseLink(receipt)}
                                  >
                                    Firestore
                                  </Button>
                                )}
                              </Box>
                              {Object.entries(receipt.products).map(([key, item]) => {
                                const size = item.sizeOfItem ?? 1;
                                const unit = getUnitType(
                                  item.unitType === 'KILOGRAM' ? 'KG' : item.unitType,
                                  companyStore.data?.customUnits,
                                ).pre;
                                const price = new Money(item.price);
                                return (
                                  <Box key={key} sx={{ marginTop: 1, marginBottom: 1 }}>
                                    <Typography variant="body1">{item.title}</Typography>
                                    <Typography variant="body2" gutterBottom component="div">
                                      {item.count
                                        .toLocaleString('sv-SE', { maximumFractionDigits: 20 })
                                        .replace(/\u2212/g, '-')}{' '}
                                      st
                                      {(size !== 1 || unit !== 'st') &&
                                        ` * ${size
                                          .toLocaleString('sv-SE', { maximumFractionDigits: 20 })
                                          .replace(/\u2212/g, '-')} ${unit}`}
                                      {` * ${price.format()} = ${price
                                        .multiply(item.count)
                                        .multiply(size)
                                        .format()}`}
                                    </Typography>
                                  </Box>
                                );
                              })}
                              <Box sx={{ paddingTop: 2 }}>
                                <Typography>
                                  {`
                              Betalmetod:
                              ${getReceiptPaymentMethod(receipt)}`}
                                </Typography>
                                {!!receipt.cashRegisterLabel && (
                                  <>
                                    <Typography>
                                      Kontrollenhet serienummer: {receipt.controlUnitSerial}
                                    </Typography>
                                    <Typography>
                                      Kassaregister: {receipt.cashRegisterLabel}
                                    </Typography>
                                  </>
                                )}
                                {!!receipt.bookkeepingData && (
                                  <Typography>
                                    Bokfört i verifikat: {receipt.bookkeepingData.voucherSeries}
                                    {receipt.bookkeepingData.voucherNumber}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
              </TableBody>
            </Table>
            <Box sx={{ flexDirection: 'row', display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <Box sx={{ flexDirection: 'row', display: 'flex' }}>
                <TablePagination
                  count={section.receipts.length}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  component={Box}
                  rowsPerPage={ROWS_PER_PAGE}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                />
              </Box>
            </Box>
          </TableContainer>
        )}
      </Box>
    );

    if (!showRegisterInfo) {
      return content;
    }

    return (
      <Accordion defaultExpanded={section.register?.isCurrent ?? !section.register}>
        <AccordionSummary
          expandIcon={<KeyboardArrowDownIcon />}
          id={`${section.key}-header`}
          aria-controls={`${section.key}-content`}
          sx={{ bgcolor: 'action.hover' }}
        >
          <Box>
            <Typography fontWeight="bold">
              {section.label}
              {section.register &&
                ` - ${
                  section.register.isCurrent ? 'Aktuellt kassaregister' : 'Tidigare kassaregister'
                }`}
              {` - ${section.receipts.length} inlästa kvitton`}
            </Typography>
            <Typography variant="body2">
              {`Aktiverat: ${
                activatedAt
                  ? DateTime.fromJSDate(activatedAt).toFormat('yyyy-MM-dd HH:mm')
                  : 'Okänt'
              } `}
              {section.register?.serial && `Serienummer: ${section.register.serial} `}
            </Typography>
            {!section.register && section.label !== 'Utan kassaregister' && (
              <Typography variant="body2">Kassaregister saknas i registerhistoriken</Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>{content}</AccordionDetails>
      </Accordion>
    );
  },
);

const Receipts = observer(() => {
  const companyStore = useCompany();
  const receiptStore = useReceipt();
  const history = useRegisterZHistory(companyStore.companyKey || '');
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadMoreError, setLoadMoreError] = useState('');
  const [showPreviousRegisters, setShowPreviousRegisters] = useState(false);

  useEffect(() => {
    setShowPreviousRegisters(false);
    receiptStore.fetchInitialReceipts();
  }, [companyStore.companyKey]);

  const sections = groupReceiptsByRegister(receiptStore.list, history.data || []).filter(
    (section) => showPreviousRegisters || section.register?.isCurrent,
  );

  const loadMoreReceipts = async () => {
    setLoadMoreError('');
    try {
      await receiptStore.fetchMoreReceipts();
    } catch (error) {
      setLoadMoreError(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Box>
      <Toolbar disableGutters role="toolbar" aria-label="Kvittofilter">
        <FormControlLabel
          control={
            <Checkbox
              checked={showPreviousRegisters}
              onChange={(_, checked) => setShowPreviousRegisters(checked)}
            />
          }
          label="Visa tidigare kvitton från tidigare kassaregister"
        />
      </Toolbar>
      <Typography variant="h1" gutterBottom>
        Kvitton
      </Typography>

      <Paper sx={{ my: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h5">
            {showPreviousRegisters ? 'Kvitton per kassaregister' : 'Kvitton'}
          </Typography>
          <IconButton
            aria-label="Uppdatera kvitton"
            size="small"
            disabled={receiptStore.loading || history.isLoading}
            onClick={() => {
              receiptStore.refreshReceipts();
              setLoadMoreError('');
              runInAction(() => {
                history.error = undefined;
              });
              history.refetch();
              setRefreshKey((current) => current + 1);
            }}
          >
            <RefreshRounded />
          </IconButton>
        </Box>
        {history.error && (
          <Alert severity="error">Misslyckades med att hämta registerhistorik</Alert>
        )}
        {loadMoreError && (
          <Alert severity="error">Misslyckades med att hämta fler kvitton: {loadMoreError}</Alert>
        )}
        <LoadingWrapper
          loading={(receiptStore.loading && receiptStore.list.length === 0) || history.isLoading}
        >
          <Box>
            {sections.map((section) => (
              <ReceiptSection
                key={`${companyStore.companyKey}-${refreshKey}-${section.key}`}
                section={section}
                showRegisterInfo={showPreviousRegisters}
              />
            ))}
            {sections.length === 0 && !history.error && (
              <Typography>Det finns inga kvitton att visa.</Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              {receiptStore.loading ? (
                <CircularProgress color="secondary" />
              ) : (
                !receiptStore.endReached && (
                  <Button onClick={loadMoreReceipts}>Läs in fler kvitton</Button>
                )
              )}
            </Box>
          </Box>
        </LoadingWrapper>
      </Paper>
    </Box>
  );
});

export default Receipts;
